/**
 * CreditLogsModal - 积分明细弹窗
 * 用户查看自己的积分消费记录
 * 管理员可以传入 userId 和 userName 查看指定用户的记录
 */

import React, { useState, useEffect, useCallback } from 'react';
import { X, ArrowDown, ArrowUp, RefreshCw, Gift, Sparkles, Sparkle, Trash2, CreditCard, Ticket } from 'lucide-react';
import api from '../api';
import { IS_EDU, CHARGE_WORD } from '../config/edition'; // 🎓 教育版隐藏充值入口 + 改口叫「发放」

const typeLabels = {
  charge: { text: CHARGE_WORD, color: '#3b82f6', icon: ArrowDown },
  consume: { text: '消费', color: '#94a3b8', icon: ArrowUp },
  refund: { text: '退款', color: '#0ea5e9', icon: RefreshCw },
  register: { text: '注册赠送', color: '#6366f1', icon: Gift },
  // 🎓 算力券兑换（只有教育版会产生这种流水）。两个版本都留着这一项：
  //    万一教育版用户的数据将来出现在别处，也不至于显示成裸的 'voucher'。
  voucher: { text: '算力券', color: '#10b981', icon: Ticket }
};

// 格式化时间为北京时间字符串；无效返回提示文案
function formatTime(value) {
  try {
    const date = new Date(value);
    if (isNaN(date)) return '时间未知';
    return date.toLocaleString('zh-CN', {
      timeZone: 'Asia/Shanghai',
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', second: '2-digit',
      hour12: false
    });
  } catch (e) {
    return '时间解析失败';
  }
}

// AI 生成正常耗时上限（毫秒）；发起超过此时长仍无完成时间，视为"已完成但漏标"而非"生成中"
const GENERATION_TIMEOUT_MS = 30 * 60 * 1000;

// 计算消费记录的"完成"行展示内容
//   - 有 completed_at        → 具体完成时间
//   - 无 completed_at + 30min 内 → 生成中（大概率仍在跑）
//   - 无 completed_at + 超 30min → 已完成（生成不会这么久，必为漏标的历史/分支数据）
function getCompletionDisplay(log) {
  if (log.completed_at) {
    return { text: `完成: ${formatTime(log.completed_at)}`, color: '#64748b' };
  }
  const startedMs = new Date(log.created_at).getTime();
  if (!isNaN(startedMs) && Date.now() - startedMs < GENERATION_TIMEOUT_MS) {
    return { text: '生成中…', color: '#fbbf24' };
  }
  return { text: '已完成', color: '#64748b' };
}

export default function CreditLogsModal({ open, onClose, userId, userName }) {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [confirmReset, setConfirmReset] = useState(false);
  const [resetting, setResetting] = useState(false);
  const limit = 20;

  const loadLogs = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit });
      if (userId) {
        params.append('user_id', userId);
      }
      const data = await api.get(`/api/user/credit-logs?${params.toString()}`);
      setLogs(data.logs || []);
      setTotal(data.total || 0);
    } catch (err) {
      console.error('加载积分明细失败:', err);
    } finally {
      setLoading(false);
    }
  }, [page, userId]);

  useEffect(() => {
    if (!open) return;
    setPage(1); // 重置页码
  }, [open, userId]);
  
  useEffect(() => {
    if (!open) return;
    loadLogs();
  }, [open, page, userId, loadLogs]);

  if (!open) return null;

  const totalPages = Math.ceil(total / limit);

  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      background: 'rgba(0,0,0,0.5)', zIndex: 1000,
      display: 'flex', alignItems: 'center', justifyContent: 'center'
    }}>
      <div style={{
        width: 500, maxHeight: '80vh', background: 'var(--modal-surface)',
        borderRadius: 12, border: '1px solid var(--border-subtle)',
        display: 'flex', flexDirection: 'column'
      }}>
        {/* Header */}
        <div style={{
          padding: '16px 20px', borderBottom: '1px solid rgba(255,255,255,0.08)',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <Sparkle size={20} color="#fbbf24" strokeWidth={2.5} />
            <h3 style={{ margin: 0, fontSize: 16, color: 'var(--text-primary)' }}>
              {userName ? `${userName} 的积分明细` : '积分明细'}
            </h3>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            {!userId && !IS_EDU && (
              <button
                onClick={() => { onClose(); setTimeout(() => window.dispatchEvent(new CustomEvent('open-recharge')), 100); }}
                title="积分充值"
                style={{
                  background: 'linear-gradient(135deg, rgba(234,179,8,0.2), rgba(234,179,8,0.1))',
                  border: '1px solid rgba(234,179,8,0.3)',
                  padding: '3px 10px', borderRadius: 6, cursor: 'pointer',
                  color: '#facc15', fontSize: 12, fontWeight: 600,
                  display: 'flex', alignItems: 'center', gap: 4,
                  transition: 'all 0.15s'
                }}
                onMouseEnter={e => { e.currentTarget.style.background = 'linear-gradient(135deg, rgba(234,179,8,0.3), rgba(234,179,8,0.2))'; }}
                onMouseLeave={e => { e.currentTarget.style.background = 'linear-gradient(135deg, rgba(234,179,8,0.2), rgba(234,179,8,0.1))'; }}
              >
                <CreditCard size={13} />
                充值
              </button>
            )}
            {/* 🎓 教育版顶替上面那颗按钮：积分不够时去兑换老师发的算力券（免费） */}
            {!userId && IS_EDU && (
              <button
                onClick={() => { onClose(); setTimeout(() => window.dispatchEvent(new CustomEvent('open-voucher-redeem')), 100); }}
                title="兑换算力券"
                style={{
                  background: 'linear-gradient(135deg, rgba(16,185,129,0.2), rgba(16,185,129,0.1))',
                  border: '1px solid rgba(16,185,129,0.3)',
                  padding: '3px 10px', borderRadius: 6, cursor: 'pointer',
                  color: '#34d399', fontSize: 12, fontWeight: 600,
                  display: 'flex', alignItems: 'center', gap: 4,
                  transition: 'all 0.15s'
                }}
                onMouseEnter={e => { e.currentTarget.style.background = 'linear-gradient(135deg, rgba(16,185,129,0.3), rgba(16,185,129,0.2))'; }}
                onMouseLeave={e => { e.currentTarget.style.background = 'linear-gradient(135deg, rgba(16,185,129,0.2), rgba(16,185,129,0.1))'; }}
              >
                <Ticket size={13} />
                兑换
              </button>
            )}
            {!confirmReset ? (
              <button
                onClick={() => setConfirmReset(true)}
                title="清空积分记录"
                style={{
                  background: 'transparent', border: 'none',
                  padding: 4, cursor: 'pointer', color: 'var(--text-muted)',
                  display: 'flex', alignItems: 'center',
                  transition: 'color 0.15s'
                }}
                onMouseEnter={e => e.currentTarget.style.color = '#ef4444'}
                onMouseLeave={e => e.currentTarget.style.color = '#666'}
              >
                <Trash2 size={16} />
              </button>
            ) : (
              <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                <span style={{ fontSize: 11, color: '#fca5a5' }}>确认清空？</span>
                <button
                  onClick={async () => {
                    setResetting(true);
                    try {
                      await api.delete(`/api/user/credit-logs${userId ? `?user_id=${userId}` : ''}`);
                      setLogs([]);
                      setTotal(0);
                      setPage(1);
                    } catch (err) {
                      console.error('清空积分记录失败:', err);
                    } finally {
                      setResetting(false);
                      setConfirmReset(false);
                    }
                  }}
                  disabled={resetting}
                  style={{
                    background: 'rgba(239,68,68,0.15)', border: '1px solid rgba(239,68,68,0.3)',
                    padding: '2px 8px', borderRadius: 4, cursor: 'pointer',
                    color: '#ef4444', fontSize: 11, fontWeight: 600
                  }}
                >
                  {resetting ? '...' : '确定'}
                </button>
                <button
                  onClick={() => setConfirmReset(false)}
                  style={{
                    background: 'rgba(255,255,255,0.05)', border: '1px solid var(--border-subtle)',
                    padding: '2px 8px', borderRadius: 4, cursor: 'pointer',
                    color: 'var(--text-secondary)', fontSize: 11
                  }}
                >
                  取消
                </button>
              </div>
            )}
            <button
              onClick={onClose}
              style={{
                background: 'transparent', border: 'none',
                padding: 4, cursor: 'pointer', color: 'var(--text-muted)'
              }}
            >
              <X size={20} />
            </button>
          </div>
        </div>

        {/* Content */}
        <div style={{ flex: 1, overflow: 'auto', padding: '16px 20px' }}>
          {loading ? (
            <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)' }}>
              加载中...
            </div>
          ) : logs.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)' }}>
              暂无积分记录
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {logs.map(log => {
                const typeInfo = typeLabels[log.type] || { text: log.type, color: '#94a3b8', icon: Sparkles };
                const TypeIcon = typeInfo.icon;
                const isPositive = log.amount > 0;
                
                return (
                  <div
                    key={log.id}
                    style={{
                      padding: '12px 16px', borderRadius: 8,
                      background: 'var(--modal-input-bg)',
                      border: '1px solid rgba(255,255,255,0.05)',
                      display: 'flex', justifyContent: 'space-between', alignItems: 'center'
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, flex: 1, minWidth: 0 }}>
                      <div style={{
                        width: 36, height: 36, borderRadius: 8,
                        background: `${typeInfo.color}15`,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        flexShrink: 0
                      }}>
                        <TypeIcon size={18} style={{ color: typeInfo.color }} />
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ 
                          fontSize: 13, color: 'var(--text-body)', marginBottom: 4,
                          whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' 
                        }}>
                          {log.reason || typeInfo.text}
                        </div>
                        <div style={{ fontSize: 11, color: '#64748b', display: 'flex', flexDirection: 'column', gap: 2 }}>
                          {log.model_name && <span>模型: {log.model_name}</span>}
                          <span>发起: {formatTime(log.created_at)}</span>
                          {/* 仅消费记录有"完成"概念；充值/退款/赠送是瞬时记录，不显示完成行 */}
                          {log.type === 'consume' && (() => {
                            const completion = getCompletionDisplay(log);
                            return <span style={{ color: completion.color }}>{completion.text}</span>;
                          })()}
                        </div>
                      </div>
                    </div>
                    <div style={{ textAlign: 'right', flexShrink: 0, marginLeft: 16 }}>
                      <div style={{
                        fontSize: 16, fontWeight: 600,
                        color: isPositive ? '#06b6d4' : '#3b82f6'
                      }}>
                        {isPositive ? '+' : ''}{Math.round(log.amount)}
                      </div>
                      <div style={{ fontSize: 11, color: '#64748b' }}>
                        余额: {Math.round(log.balance_after)}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div style={{
            padding: '12px 20px', borderTop: '1px solid rgba(255,255,255,0.08)',
            display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 8
          }}>
            <button
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1}
              style={{
                background: 'rgba(255,255,255,0.05)', border: 'none',
                padding: '6px 12px', borderRadius: 6, cursor: 'pointer',
                color: page === 1 ? '#444' : 'var(--text-secondary)', fontSize: 12
              }}
            >
              上一页
            </button>
            <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>
              {page} / {totalPages}
            </span>
            <button
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
              style={{
                background: 'rgba(255,255,255,0.05)', border: 'none',
                padding: '6px 12px', borderRadius: 6, cursor: 'pointer',
                color: page === totalPages ? '#444' : 'var(--text-secondary)', fontSize: 12
              }}
            >
              下一页
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
